import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Spinner } from '@chakra-ui/react';
import { NormalUserContainer } from '../../styleComponents/NormalUserStyle';
import KpiMiniTable from '../../sections/KpiMiniTable';
import Axios from '../../utils/httpClient';

const CheckApps = () => {
    const { user } = useSelector(s => s);
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadApps();
    }, []);

    const loadApps = async () => {
        try {
            setLoading(true);
            // Load ALL applications for statistics (all time, not just current month)
            const res = await Axios().get('api/v1/application/list/?all_time=true');
            setApps(res?.data?.applications || []);
        } catch (error) {
            console.error('Error loading applications:', error);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to check if application belongs to this department
    const belongsToDepartment = (app) => {
        if (user?.role === 3) return true; // Pro Rector sees everything
        const code = app?.parameter?.code;
        const departmentName = user?.partition?.name?.toLowerCase() || "";

        const isTT = departmentName.includes("tarmoq texnologiyalari");
        const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
        const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
        const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
        const isXL = departmentName.includes("xalqaro hamkorlik");
        const isIL = departmentName.includes("ilmiy bo'lim");

        // For KPI 1.1 (multi-department: TT, TS, OV)
        if (code === '1.1') {
            return (isTT || isTS || isOV);
        }
        // For KPI 1.3, 1.4 (multi-department: MN, OV)
        else if (code === '1.3' || code === '1.4') {
            return (isMN || isOV);
        }
        // For KPI 3.2, 3.3 (multi-department: OV, XL, IL)
        else if (code === '3.2' || code === '3.3') {
            return (isOV || isXL || isIL);
        }
        // For KPI 3.4 (multi-department: XL, IL)
        else if (code === '3.4') {
            return (isXL || isIL);
        }
        // For KPI 3.1, 2.1 (multi-department: XL, IL)
        else if (code === '3.1' || code === '2.1') {
            return (isXL || isIL);
        }
        // For single-department KPIs, check if the parameter belongs to this department
        else {
            // 1.2, 1.5: Ta'lim sifati bo'limi
            if (code === '1.2' || code === '1.5') {
                return isTS;
            }
            // 1.6-1.9: O'quv bo'limi
            if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
                return isOV;
            }
            // 2.x (except 2.1, 2.2): Ilmiy bo'lim
            if (code && code.startsWith('2.') && code !== '2.1' && code !== '2.2') {
                return isIL;
            }
            // 4.x: Ma'naviyat bo'limi
            if (code && code.startsWith('4.')) {
                return isMN;
            }
            // Fallback: don't show (partition_id is not reliable)
            return false;
        }
    };

    // Calculate statistics for department head
    const departmentApps = apps.filter(belongsToDepartment);

    // Helper to check if THIS department approved/rejected the application
    const isDepartmentApproved = (app) => {
        if (user?.role === 3) return app.status === 4;
        const departmentName = user?.partition?.name?.toLowerCase() || "";
        const isTT = departmentName.includes("tarmoq texnologiyalari");
        const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
        const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
        const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
        const isIL = departmentName.includes("ilmiy bo'lim");

        // Check department-specific status fields
        if (isTT) return app.tt_status === 1;
        if (isTS) return app.ts_status === 1;
        if (isOV) return app.ov_status === 1;
        if (isMN) return app.mn_status === 1;
        if (isIL) return app.ts_status === 1; // IL uses ts_status field
        return false;
    };

    const isDepartmentRejected = (app) => {
        if (user?.role === 3) return app.status === 5;
        const departmentName = user?.partition?.name?.toLowerCase() || "";
        const isTT = departmentName.includes("tarmoq texnologiyalari");
        const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
        const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
        const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
        const isIL = departmentName.includes("ilmiy bo'lim");

        // Check department-specific status fields
        if (isTT) return app.tt_status === 2;
        if (isTS) return app.ts_status === 2;
        if (isOV) return app.ov_status === 2;
        if (isMN) return app.mn_status === 2;
        if (isIL) return app.ts_status === 2; // IL uses ts_status field
        return false;
    };

    const statusCounts = {
        // All applications that came to this department (ALL statuses except 6=requirement)
        kelibTushganlar: departmentApps.filter(app => app.status !== 6).length,
        // Applications approved by THIS department
        tasdiqlanganlar: departmentApps.filter(app => isDepartmentApproved(app)).length,
        // Applications rejected by THIS department
        radEtilganlar: departmentApps.filter(app => isDepartmentRejected(app)).length
    };

    return (<>
        <NormalUserContainer>
            {/* Statistics Card for Department Head */}
            <Card style={{ marginBottom: '9px', padding: '20px', marginTop: "-2px" }}>
                <div className="status-categories" style={{ marginTop: "20px" }}>
                    <div className="status-item bajarilganlar">
                        <span className="status-label">Kelib tushganlar:</span>
                        <span className="status-count">{statusCounts.kelibTushganlar}</span>
                    </div>
                    <div className="status-item tasdiqlanganlar">
                        <span className="status-label">Tasdiqlanganlar:</span>
                        <span className="status-count">{statusCounts.tasdiqlanganlar}</span>
                    </div>
                    <div className="status-item rad-etilganlar">
                        <span className="status-label">Rad etilganlar:</span>
                        <span className="status-count">{statusCounts.radEtilganlar}</span>
                    </div>
                </div>
            </Card>

            <div style={{ marginBottom: '20px' }}>
                <KpiMiniTable isDepartmentHead={true} />
            </div>
        </NormalUserContainer>
    </>)
}

export default CheckApps
